import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";

import { SprintProvider } from "./SprintContext";
import { supabase } from "./supabase";

import LiveSupabase from "./Pages/LiveSupabase";
import LiveFirebase from "./Pages/LiveFirebase";
import TagsSupabase from "./Pages/TagsSupabase";
import Home from "./Pages/Home";
import Sprint from "./Pages/Sprint";
import Sprint200 from "./Pages/Sprint200";
import Settings from "./Pages/Settings";
import TeamPursuit from "./Pages/TeamPursuit";
import VideoBrowser from "./Pages/VideoBrowser";
import VideoAutoplayLast from "./Pages/VideoAutoplayLast";
import RegistrationForm from "./Pages/Registration";
import withSite from "./withSite";
import Welcome from "./Pages/Welcome";
import End from "./Pages/End";
import LiveSession from "./Pages/LiveSession";
import Events from "./Pages/Events";

import { getSessionPhase, SESSION_PHASE, getCurrentSession } from "./Utils/SessionUtils";

import './App.css';

export const SITE_TAG = 'ghent_eddymerckx';
// export const SCREEN_MAP = {
//     'live': '/',
//     'tags': '/tags',
//     'home': '/home',
//     'sprint': '/sprint',
//     'settings': '/settings',
//     'video': '/video',
//     'video_autoplay_last': '/video_autoplay_last',
//     'video_browser': '/video_browser',
//     'register/gent': '/register/gent',
//     'register/zolder': '/register/zolder',
//     'welcome': '/welcome',
//     'end': '/end',
// };

// Screen mapping configuration: screens that the user can select
export const SCREEN_MAP = {
    'live': '/',
    'sprint 250m': '/sprint',
    'sprint 200m': '/sprint_200',
    'teampursuit': "/teampursuit"
};

const mapScreenTag = (screenTag) => Object.keys(SCREEN_MAP).includes(screenTag) ? SCREEN_MAP[screenTag] : '/';

const sites = {
    'ghent_eddymerckx': { name: 'Wielercentrum Eddy Merckx' },
    'zolder_velodroom': { name: 'Velodroom Zolder' },
};

const RegistrationWithSiteGent = withSite(RegistrationForm, sites['ghent_eddymerckx']);
// const RegistrationWithSiteZolder = withSite(RegistrationForm, sites['zolder_velodroom']);

function App() {
    const navigate = useNavigate();
    const location = useLocation();

    const [screenTag, setScreenTag] = useState('/');
    const [currentSession, setCurrentSession] = useState(null);
    const [sessionPhase, setSessionPhase] = useState(false); // start, mid, end
    const [venueId, setVenueId] = useState(null);
    const [venueTag, setVenueTag] = useState(null);

    useEffect(() => {
        const initializeVenueId = async () => {
            const params = new URLSearchParams(location.search);
            const vId = parseInt(params.get('venue_id'));
            // console.log("vId", vId);
            if (vId) {
                 setVenueId(vId);
            } else {
                const defaultVenueId = 1; // Default to 1 if no venue_id is provided
                setVenueId(defaultVenueId);
            }
        }

        initializeVenueId();
    }, []);

    // Get venue ID from URL or use default
    useEffect(() => {
        const fetchSiteTag = async () => {
            try {
                // console.log("venueId", venueId);
                if (venueId == null) {
                    return;
                }
                const { data, error } = await supabase
                    .from("screen_configuration")
                    .select("site")
                    .eq("id", venueId)
                    .single();

                if (error) {
                    console.error("Error fetching site tag:", error);
                    return;
                }

                if (data) {
                    console.log("Fetched site tag:", data.site);
                    const vTag = data.site;
                    setVenueTag(vTag);
                }
            } catch (err) {
                console.error("Unexpected error fetching site tag:", err);
            }
        };

        fetchSiteTag();
    }, [venueId]);


    // Helper function to check if redirection should be bypassed (for testing)
    const shouldBypassRedirect = () => {
        const params = new URLSearchParams(location.search);
        return params.get('bypass_redirect') === 'true';
    };

    // Helper function to add venue ID to URL
    const getUrlWithVenue = (baseUrl) => {
        return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}venue_id=${venueId}`;
    };

    useEffect(() => {
        const fetchScreenConfig = async () => {
            if (venueId === null) {
                return;
            }
            const { data, error } = await supabase
                .from("screen_configuration")
                .select("*")
                .eq("id", venueId).single();

            if (error) {
                console.error(error);
                return;
            }

            if (data.length === 0) {
                console.error("No screen configuration found for site", venueId);
                return;
            }

            let active_tag = mapScreenTag(data.screen_tag);
            //active_tag = "teampursuit"
            setScreenTag(active_tag); 
        };

        fetchScreenConfig();
    }, [venueId])

    useEffect(() => {
        if(venueId === null) {
            return;
        }
        const taskListener = supabase.channel('screen_configuration').on(
            "postgres_changes",
            { event: "UPDATE", schema: "public", table: "screen_configuration" },
            (payload) => {
                const { site, screen_tag: screenTag } = payload.new;
                if (venueId !== parseInt(payload.new.id)){ 
                    console.log("site", site, "venueId", venueId);
                    return;
                }
                let active_tag = mapScreenTag(screenTag);
                //active_tag = "teampursuit"
                setScreenTag(active_tag);
            },
        ).subscribe();

        return () => taskListener.unsubscribe();
    }, [venueId]);

    // New effect to check current session and manage automatic redirection
    useEffect(() => {
        const checkCurrentSession = async () => {
            try {
                if (venueId === null) {
                    return;
                }
                
                // Find current session
                const activeSession = await getCurrentSession(null, venueId);

                if (activeSession) {
                    setCurrentSession(activeSession);

                    // Get current phase
                    const phase = getSessionPhase(activeSession);
                    setSessionPhase(phase);

                    console.log('Current session:', activeSession);
                    console.log('Current phase:', phase);

                    // Check if redirects should be bypassed (for testing)
                    if (shouldBypassRedirect()) {
                        return;
                    }

                    // Check if the current location needs group session redirection
                    const isOnMainPage = (location.pathname === '/' || location.pathname === screenTag);
                    const isOnWelcomeOrEnd = location.pathname === '/welcome' || location.pathname === '/end';
                    const needsGroupSessionRedirection = isOnMainPage || isOnWelcomeOrEnd;

                    if (!needsGroupSessionRedirection) {
                        return;
                    }

                    if (activeSession?.is_group_event || false) { // Only redirect if it's a group session
                        // Handle navigation based on session phase
                        if (phase === SESSION_PHASE.START) {
                            // First 15 minutes - show welcome screen
                            navigate(getUrlWithVenue('/welcome'));
                        } else if (phase === SESSION_PHASE.END) {
                            // Last 5 minutes - show end screen
                            navigate(getUrlWithVenue('/end'));
                        } else if (phase === SESSION_PHASE.MID && isOnWelcomeOrEnd) {
                            // In mid phase, navigate back to main page if currently on welcome or end
                            navigate(getUrlWithVenue(screenTag));
                        }
                    }
                } else {
                    // No current session
                    setCurrentSession(null);
                    setSessionPhase(null);
                }
            } catch (err) {
                console.error('Error checking sessions:', err);
            }
        };

        // Check immediately and then every minute
        checkCurrentSession();
        const interval = setInterval(checkCurrentSession, 60 * 1000);

        return () => clearInterval(interval);
    }, [navigate, location.pathname, screenTag, venueId]);

    // Effect to handle screen selection redirection from settings page
    useEffect(() => {
        // console.log("trying to change screenTag to: ", screenTag);
        const validPaths = Object.values(SCREEN_MAP);

        // console.log("venueId is null?", venueId, (venueId === null))
        if(venueId === null) {
            return;
        }
        // If on any screen in SCREEN_MAP that's not the currently selected screenTag,
        // redirect to the selected screenTag (configured via settings)
        if (validPaths.includes(location.pathname) && location.pathname !== screenTag) {
            navigate(getUrlWithVenue(screenTag));
        } else if (location.pathname === '/' && screenTag !== '/') {
            // Also redirect from root path if screenTag is not root
            navigate(getUrlWithVenue(screenTag));
        }
    }, [screenTag, venueId]);

    return (
        <div className="App">
            <main style={{ width: "100%" }}>
                <Routes>
                    <Route path="/" element={<LiveSupabase currentSession={currentSession} venueId={venueId} orderby="numLaps"/>}/>
                    <Route path="/latest" element={<LiveSupabase currentSession={currentSession} venueId={venueId} orderby="latest"/>}/>
                    <Route path="/live_firebase" element={<LiveFirebase />} />
                    <Route path="/tags" element={<TagsSupabase />} />
                    <Route path="/home" element={<Home />} />
                    <Route path="/sprint" element={<SprintProvider currentSession={currentSession} venueId={venueId}><Sprint /></SprintProvider>} />
                    <Route path="/sprint_200" element={<SprintProvider currentSession={currentSession} venueId={venueId}><Sprint200 /></SprintProvider>} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/teampursuit" element={<SprintProvider currentSession={currentSession} venueId={venueId}><TeamPursuit /></SprintProvider>} />
                    <Route path="/sprint_fixed" element={<SprintProvider currentSession={currentSession} venueId={venueId}><Sprint /></SprintProvider>} />
                    <Route path="/video_browser" element={<VideoBrowser />} />
                    <Route path="/video_autoplay_last" element={<VideoAutoplayLast />} />
                    <Route path="/register" element={<RegistrationForm />} />
                    <Route path="/register/gent" element={<RegistrationWithSiteGent />} />
                    <Route path="/register/edit/:editToken" element={<RegistrationWithSiteGent />} />
                    <Route path="/welcome" element={<Welcome />} />
                    <Route path="/end" element={<End />} />
                    <Route path="/single" element={<LiveSupabase currentSession={currentSession} venueId={venueId} orderby="numLaps" transponderId={"RR01344"}/>}/>
                    <Route path="/single_session" element={<LiveSession currentSession={currentSession} venueId={venueId} orderby="latest" transponderId={"RR01344"}/>}/>
                    <Route path="/events" element={<Events />} />
                    <Route path="/events/:venueId" element={<Events />} />


                    {/* <Route path="/register/zolder" element={<RegistrationWithSiteZolder />} /> */}
                </Routes>
            </main>

        </div>
    );
}

export default App;
